import React from "react";
import Link from "components/MDX/Link";
import SnippetSpan from "components/SnippetSpan";
import HTTPMethod from "components/HTTPMethod";
import Paragraphs from "components/Paragraphs";
import NextPrevNav from "components/NextPrevNav";
import Chat from "components/MDX/Chat";
import ListNodes from "components/ListNodes";
import DiscordLink from "components/DiscordLink";
import Callout from "components/Callout";
import classnames from "classnames";
import { getJWT } from "utils/auth";
import APIKeysAccessPrompt from "components/APIKeysAccessPrompt";
import { useAuthedDocsPreview } from "utils/hooks/docs";
import isEmpty from "lodash/isEmpty";
import Padder from "components/Padder";
import { NODE_UID } from "constants/docs";

const COMPONENT_MAP = {
  [NODE_UID.CALLOUT]: Callout
};

const ResourceOverview = ({
  navConfig,
  endpoints,
  pathname,
  description: { title, paragraphs },
  nodes
}) => {
  const resources = endpoints ? endpoints.resources : null;
  const { preview: authedPreview } = useAuthedDocsPreview({
    pathname
  });
  const needsTestKeys = isEmpty(authedPreview);
  const isUser = Boolean(getJWT());

  const contentNodes = [];
  if (paragraphs) {
    contentNodes.push(
      <Paragraphs
        paragraphs={paragraphs}
        customClasses={{
          block: "lh-copy pt1 f5"
        }}
      />
    );
  }
  contentNodes.push(
    <Chat
      header=""
      subheader="Have any questions or feedback?"
      actionCopy="Chat"
    >
      <DiscordLink copy="or join us on Discord" />
    </Chat>
  );
  if (navConfig) {
    contentNodes.push(
      <div className="bt w-100 hairline-1 pt3">
        <NextPrevNav
          config={navConfig}
          customClasses={{
            directionLabel: "f6 black-70",
            parentLabel: "fw5 f5 pt2",
            nextLabel: "f6 fw6 color-primary"
          }}
        />
      </div>
    );
  }

  const sidebarNodes = [];
  if (resources) {
    sidebarNodes.push(
      <div className="w-100 bg-white ba hairline-1 br3 shadow-0">
        <div className="pa2 ma1 fw6 f7 ttu">Endpoints</div>
        <div className="bt w-100 hairline-1 flex flex-column">
          <ul className="list ma0 pa2 ma1">
            {resources.map(({ method, href, copy }, resourceIx) => {
              return (
                <li key={resourceIx} className="flex flex-row w-100 pv1">
                  <div className="w3 fw5">
                    <HTTPMethod method={method} />
                  </div>
                  <div className="f6 pl1">
                    <Link
                      href={href}
                      style={{
                        textDecoration: "none"
                      }}
                    >
                      <SnippetSpan clickable copy={copy} />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  sidebarNodes.push(
    <APIKeysAccessPrompt needsTestKeys={needsTestKeys} isUser={isUser} />
  );

  if (Array.isArray(nodes) && nodes.length) {
    nodes.forEach(({ uid, ...nodeProps }, nodeIx) => {
      const NodeComp = COMPONENT_MAP[uid];
      if (NodeComp) {
        sidebarNodes.push(<NodeComp key={`node-${nodeIx}`} {...nodeProps} />);
      }
    });
  }

  sidebarNodes.push(<Padder key="padder" />);

  return (
    <div className="flex flex-column items-center w-100 pt4-l">
      <div className="flex flex-column pa4-l w-100 flex-row-l">
        <div
          style={{ flexShrink: 0 }}
          className={classnames("pa3-l pt4 pt3-l w-100", {
            "w-50-l": sidebarNodes.length
          })}
        >
          <h3 className="f3 fw6 pb3 ma0">{title}</h3>
          <ListNodes nodes={contentNodes} />
        </div>

        <div
          style={{
            flexGrow: 1,
            position: "sticky"
          }}
          className="bg-white ph0 ph3-l pb3 pv4 w-100 w-50-l dib self-start sticky top-0"
        >
          <div className="dib w-100">
            <ListNodes nodes={sidebarNodes} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceOverview;
